import { Calendar, ChevronLeft, Globe, MapPin, User } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import {
	type DispositionDecision,
	generateMatchesForAlert,
	getAlertById,
	getEntityForAlert,
	MOCK_USERS,
	type ScoreLevel,
} from '@/data/aml/mockData';
import { cn } from '@/lib/utils';
import { Routes } from '@/routes/routes';

function scoreClass(level: ScoreLevel): string {
	if (level === 'very_high' || level === 'high')
		return 'bg-red-50 text-red-700';
	if (level === 'medium') return 'bg-orange-50 text-orange-700';
	return 'bg-green-50 text-green-700';
}

function dispositionLabel(d: DispositionDecision | string | undefined): string {
	if (!d) return '—';
	return String(d)
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (l) => l.toUpperCase());
}

function dispositionChipClass(
	d: DispositionDecision | string | undefined,
): string {
	const v = String(d || '').toLowerCase();
	if (v.includes('true')) return 'bg-red-50 text-red-700';
	if (v.includes('false')) return 'bg-green-50 text-green-700';
	if (v.includes('potential')) return 'bg-orange-50 text-orange-700';
	return 'bg-[#f0f0f0] text-[#646464]';
}

function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});
}

function ProfileField({
	icon: _Icon,
	label,
	value,
}: {
	icon: React.ElementType;
	label: string;
	value: string;
}) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-xs text-[#737373]">{label}</span>
			<span className="text-sm font-medium text-[#0A0A0A]">{value || '—'}</span>
		</div>
	);
}

export default function AlertDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const alert = useMemo(() => getAlertById(id ?? ''), [id]);
	const entity = useMemo(() => getEntityForAlert(id ?? ''), [id]);
	const matches = useMemo(() => generateMatchesForAlert(id ?? ''), [id]);

	const [selectedMatchId, setSelectedMatchId] = useState<string | null>(
		matches[0]?.id ?? null,
	);
	const [disposeOpen, setDisposeOpen] = useState(false);
	const [disposeDecision, setDisposeDecision] = useState('');
	const [assignOpen, setAssignOpen] = useState(false);
	const [selectedAssignee, setSelectedAssignee] = useState('');
	const [createCaseOpen, setCreateCaseOpen] = useState(false);
	const [optimisticDisposition, setOptimisticDisposition] =
		useState<DispositionDecision>(null);
	const [optimisticStatus, setOptimisticStatus] = useState<string | null>(null);

	const selectedMatch = matches.find((m) => m.id === selectedMatchId) ?? null;

	if (!alert) {
		return (
			<div className="min-h-screen bg-background p-8">
				<p className="text-sm text-[#737373]">Alert not found.</p>
			</div>
		);
	}

	const effectiveStatus = optimisticStatus ?? alert.status;
	const effectiveDisposition =
		optimisticDisposition ?? alert.disposition_decision;
	const isDisposed =
		effectiveStatus === 'disposed' ||
		effectiveStatus === 'closed' ||
		!!effectiveDisposition;

	const handleDispose = () => {
		if (!disposeDecision) {
			toast.error('Select a disposition');
			return;
		}
		setOptimisticDisposition(disposeDecision as DispositionDecision);
		setOptimisticStatus('disposed');
		setDisposeOpen(false);
		toast.success('Alert disposed successfully');
	};

	return (
		<div className="flex min-h-screen flex-col bg-background">
			<Toaster richColors position="top-right" />

			{/* Header */}
			<div className="border-b border-border bg-white px-6 py-3">
				<div className="flex items-center gap-2 mb-2">
					<button
						type="button"
						onClick={() => navigate(Routes.AML_ALERTS)}
						className="flex items-center gap-1 text-sm text-[#0070C9] hover:underline"
					>
						<ChevronLeft className="h-4 w-4" /> Alerts
					</button>
					<span className="text-[#A3A3A3]">›</span>
					<span className="text-sm font-medium text-[#0A0A0A]">
						{alert.code}
					</span>
				</div>
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="flex flex-wrap items-center gap-2">
						<h1 className="text-lg font-semibold text-[#0A0A0A]">
							Alert Detail
						</h1>
						<span className="inline-flex items-center rounded-md bg-[#F0F0F0] px-2 py-0.5 text-xs font-medium text-[#646464]">
							{alert.module_name === 'transaction_monitoring' ? 'TM' : 'NS'}
						</span>
					</div>
					<div className="flex items-center gap-2">
						{!isDisposed && (
							<>
								<Button
									variant="outline"
									size="sm"
									onClick={() => setCreateCaseOpen(true)}
								>
									Create Case
								</Button>
								<Button
									variant="outline"
									size="sm"
									onClick={() => setAssignOpen(true)}
								>
									Assign
								</Button>
								<Button
									size="sm"
									className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90"
									onClick={() => setDisposeOpen(true)}
								>
									Dispose Alert
								</Button>
							</>
						)}
						{isDisposed && (
							<span
								className={cn(
									'inline-flex items-center rounded-md px-3 py-1 text-sm font-medium',
									dispositionChipClass(effectiveDisposition),
								)}
							>
								{dispositionLabel(effectiveDisposition)}
							</span>
						)}
					</div>
				</div>
			</div>

			<div className="flex-1 p-6 space-y-4">
				{/* Alert Summary Card */}
				<Card className="border border-[#E5E5E5]">
					<CardContent className="p-4">
						<div className="flex flex-wrap items-center gap-6">
							<div className="flex flex-col gap-1">
								<span className="text-xs text-[#737373]">Alert Code</span>
								<span className="text-sm font-semibold text-[#0A0A0A]">
									{alert.code}
								</span>
							</div>
							<div className="flex flex-col gap-1 border-l border-[#E5E5E5] pl-6">
								<span className="text-xs text-[#737373]">Status</span>
								<Badge
									className={cn(
										'w-fit text-xs',
										effectiveStatus === 'open'
											? 'bg-blue-50 text-blue-700'
											: 'bg-gray-100 text-gray-700',
									)}
								>
									{effectiveStatus.charAt(0).toUpperCase() +
										effectiveStatus.slice(1)}
								</Badge>
							</div>
							<div className="flex flex-col gap-1 border-l border-[#E5E5E5] pl-6">
								<span className="text-xs text-[#737373]">Score</span>
								<span
									className={cn(
										'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold',
										scoreClass(alert.score.level),
									)}
								>
									{alert.score.level.replace('_', ' ').toUpperCase()} ·{' '}
									{alert.score.value}%
								</span>
							</div>
							<div className="flex flex-col gap-1 border-l border-[#E5E5E5] pl-6">
								<span className="text-xs text-[#737373]">SLA</span>
								<Badge
									className={cn(
										'w-fit text-xs',
										alert.sla_status === 'breached'
											? 'bg-red-50 text-red-700'
											: 'bg-green-50 text-green-700',
									)}
								>
									{alert.sla_status === 'breached' ? 'Breached' : 'On Track'}
								</Badge>
							</div>
							<div className="flex flex-col gap-1 border-l border-[#E5E5E5] pl-6">
								<span className="text-xs text-[#737373]">Assigned To</span>
								<span className="text-sm text-[#525252]">
									{alert.assigned_to?.name ?? 'Unassigned'}
								</span>
							</div>
							<div className="flex flex-col gap-1 border-l border-[#E5E5E5] pl-6">
								<span className="text-xs text-[#737373]">Created</span>
								<span className="text-sm text-[#525252]">
									{formatDate(alert.created_at)}
								</span>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Customer Profile */}
				{entity ? (
					<Card className="border border-[#E5E5E5]">
						<CardContent className="p-4">
							<div className="mb-3 text-sm font-semibold text-[#0A0A0A]">
								Customer Profile
							</div>
							<div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
								<ProfileField
									icon={User}
									label="Name"
									value={entity.customer_name}
								/>
								<ProfileField
									icon={User}
									label="Customer ID"
									value={entity.customer_id}
								/>
								<ProfileField
									icon={User}
									label="Type"
									value={entity.customer_type}
								/>
								<ProfileField
									icon={User}
									label="Gender"
									value={entity.gender}
								/>
								<ProfileField
									icon={Calendar}
									label="Date of Birth"
									value={entity.date_of_birth}
								/>
								<ProfileField
									icon={Globe}
									label="Nationality"
									value={entity.nationality}
								/>
								<ProfileField
									icon={Globe}
									label="Country"
									value={entity.country}
								/>
								<ProfileField
									icon={MapPin}
									label="Address"
									value={entity.address}
								/>
								<div className="flex flex-col gap-1">
									<span className="text-xs text-[#737373]">PEP Status</span>
									<Badge
										variant={
											entity.pep_status === 'PEP' ? 'destructive' : 'secondary'
										}
										className="w-fit text-xs"
									>
										{entity.pep_status}
									</Badge>
								</div>
							</div>
						</CardContent>
					</Card>
				) : null}

				{/* Matches */}
				{alert.module_name === 'name_screening' && (
					<Card className="border border-[#E5E5E5]">
						<CardContent className="p-4">
							<div className="mb-3 text-sm font-semibold text-[#0A0A0A]">
								Watchlist Matches ({matches.length})
							</div>
							<div className="rounded-md border border-[#E5E5E5] overflow-hidden">
								<Table>
									<TableHeader>
										<TableRow className="bg-[#FAFAFA]">
											<TableHead className="text-xs font-semibold text-[#737373]">
												Name
											</TableHead>
											<TableHead className="text-xs font-semibold text-[#737373]">
												Score
											</TableHead>
											<TableHead className="text-xs font-semibold text-[#737373]">
												Category
											</TableHead>
											<TableHead className="text-xs font-semibold text-[#737373]">
												Nationality
											</TableHead>
											<TableHead className="text-xs font-semibold text-[#737373]">
												Source
											</TableHead>
											<TableHead className="text-xs font-semibold text-[#737373]">
												PEP
											</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{matches.map((m) => (
											<TableRow
												key={m.id}
												className={cn(
													'cursor-pointer transition-colors',
													selectedMatchId === m.id
														? 'bg-blue-50'
														: 'hover:bg-[#FAFAFA]',
												)}
												onClick={() =>
													setSelectedMatchId(
														m.id === selectedMatchId ? null : m.id,
													)
												}
											>
												<TableCell className="text-sm font-medium">
													{m.name}
												</TableCell>
												<TableCell>
													<span
														className={cn(
															'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold',
															scoreClass(m.match_level),
														)}
													>
														{m.score}%
													</span>
												</TableCell>
												<TableCell>
													<span className="text-xs text-[#525252]">
														{m.category}
													</span>
												</TableCell>
												<TableCell>
													<span className="text-xs text-[#525252]">
														{m.nationality}
													</span>
												</TableCell>
												<TableCell>
													<span className="text-xs text-[#525252]">
														{m.source}
													</span>
												</TableCell>
												<TableCell>
													<Badge
														variant={
															m.pep_status === 'PEP'
																? 'destructive'
																: 'secondary'
														}
														className="text-xs"
													>
														{m.pep_status}
													</Badge>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						</CardContent>
					</Card>
				)}

				{/* Selected Match Detail */}
				{selectedMatch && alert.module_name === 'name_screening' && (
					<Card className="border border-[#E5E5E5]">
						<CardContent className="p-4">
							<div className="mb-3 text-sm font-semibold text-[#0A0A0A]">
								Selected Match — {selectedMatch.name}
							</div>
							<div className="grid grid-cols-2 gap-3 md:grid-cols-3">
								<ProfileField
									icon={User}
									label="Name"
									value={selectedMatch.name}
								/>
								<ProfileField
									icon={Calendar}
									label="Date of Birth"
									value={selectedMatch.dob}
								/>
								<ProfileField
									icon={Globe}
									label="Nationality"
									value={selectedMatch.nationality}
								/>
								<ProfileField
									icon={User}
									label="Category"
									value={selectedMatch.category}
								/>
								<ProfileField
									icon={User}
									label="PEP Status"
									value={selectedMatch.pep_status}
								/>
								<ProfileField
									icon={User}
									label="Source"
									value={selectedMatch.source}
								/>
							</div>
							{selectedMatch.aliases.length > 0 && (
								<div className="mt-3">
									<span className="text-xs text-[#737373]">Aliases</span>
									<div className="mt-1 flex flex-wrap gap-1">
										{selectedMatch.aliases.map((a) => (
											<span
												key={a}
												className="inline-flex items-center rounded-full bg-[#F5F5F5] px-2.5 py-0.5 text-xs text-[#525252]"
											>
												{a}
											</span>
										))}
									</div>
								</div>
							)}
						</CardContent>
					</Card>
				)}
			</div>

			{/* Dispose Dialog */}
			<Dialog
				open={disposeOpen}
				onOpenChange={(v) => {
					setDisposeOpen(v);
					if (!v) setDisposeDecision('');
				}}
			>
				<DialogContent className="max-w-[480px]">
					<DialogHeader>
						<DialogTitle>Dispose Alert</DialogTitle>
						<DialogDescription>
							Select a disposition decision for this alert.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-3 py-2">
						<Select value={disposeDecision} onValueChange={setDisposeDecision}>
							<SelectTrigger className="h-9">
								<SelectValue placeholder="Select disposition" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="true_match" className="text-red-600">
									True Match
								</SelectItem>
								<SelectItem value="false_match" className="text-green-600">
									False Match
								</SelectItem>
								<SelectItem value="potential_match" className="text-orange-600">
									Potential Match
								</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDisposeOpen(false)}>
							Cancel
						</Button>
						<Button
							className="bg-black text-white hover:bg-black/90"
							disabled={!disposeDecision}
							onClick={handleDispose}
						>
							Dispose
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Assign Dialog */}
			<Dialog open={assignOpen} onOpenChange={setAssignOpen}>
				<DialogContent className="max-w-[480px]">
					<DialogHeader>
						<DialogTitle>Assign Alert</DialogTitle>
					</DialogHeader>
					<Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
						<SelectTrigger>
							<SelectValue placeholder="Select analyst" />
						</SelectTrigger>
						<SelectContent>
							{MOCK_USERS.map((u) => (
								<SelectItem key={u.id} value={u.id}>
									{u.name} ({u.role})
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<DialogFooter>
						<Button variant="outline" onClick={() => setAssignOpen(false)}>
							Cancel
						</Button>
						<Button
							className="bg-black text-white hover:bg-black/90"
							disabled={!selectedAssignee}
							onClick={() => {
								toast.success('Alert assigned');
								setAssignOpen(false);
							}}
						>
							Assign
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Create Case Dialog */}
			<Dialog open={createCaseOpen} onOpenChange={setCreateCaseOpen}>
				<DialogContent className="max-w-[480px]">
					<DialogHeader>
						<DialogTitle>Create Case</DialogTitle>
						<DialogDescription>
							Group this alert into a new or existing case.
						</DialogDescription>
					</DialogHeader>
					<p className="text-sm text-[#737373]">
						Alert {alert.code} will be added to the new case.
					</p>
					<DialogFooter>
						<Button variant="outline" onClick={() => setCreateCaseOpen(false)}>
							Cancel
						</Button>
						<Button
							className="bg-black text-white hover:bg-black/90"
							onClick={() => {
								toast.success('Case created successfully');
								setCreateCaseOpen(false);
							}}
						>
							Create Case
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
